import { useCallback, useEffect, useRef, useState } from 'react';
import { useOptionalBriefingRoomServices } from '../contexts/BriefingRoomContext';
import { useNarratorVoice } from './useNarratorVoice';

/**
 * V4 Voice GM (2026-06-02) — sequential TTS playback queue.
 *
 * Central place that owns TTS playback so the V4 mic-cancels-tts magic
 * AND the V3-rationale → V4-paragraph chaining both work with one
 * cancel surface. Pattern: enqueue() plays now or queues if busy;
 * cancelAll() stops mid-sentence and clears the queue.
 *
 * Why a queue (not a plain "speak"):
 *   - V3 dispatches a tool call → wants to speak the rationale.
 *   - V4 then auto-narrates the new paragraph.
 *   - Both fire close together; the cinematic feel needs them
 *     sequenced, not racing.
 *   - V4 mic-press needs ONE cancel-everything call regardless of
 *     which speak fired it.
 *
 * Cancel + cleanup semantics:
 *   - cancelAll() empties the pending queue, pauses the active audio,
 *     revokes the active blob URL, sets state to 'idle'.
 *   - Audio.onended fires the next item or transitions to 'idle'.
 *   - Network errors (services.voice.speak rejects) are caught + the
 *     queue moves on to the next item.
 */

// V4.7 (2026-06-02): added 'loading' between enqueue and audio.play()
// so the UI can show a spinner during the Kokoro fetch round-trip
// (Kokoro takes ~1-5s to synthesize). State transitions:
//   idle → loading (speak() in flight) → playing (audio.play() called)
//     → loading (next item) OR idle (queue drained)
//   * any → cancelled (cancelAll) → idle (microtask)
export type TtsQueueState = 'idle' | 'loading' | 'playing' | 'cancelled';

export interface TtsEnqueueOptions {
  voice?: string;
  /** Tag for debug + tests; not surfaced in UI. */
  source?: string;
}

interface QueueItem {
  text: string;
  voice?: string;
  source?: string;
}

export function useTtsQueue() {
  const services = useOptionalBriefingRoomServices();
  const [defaultVoice] = useNarratorVoice();
  const [state, setState] = useState<TtsQueueState>('idle');
  const queueRef = useRef<QueueItem[]>([]);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const activeRevokeRef = useRef<(() => void) | null>(null);
  const cancelTokenRef = useRef<number>(0);
  // Synchronous "currently dispatching a speak()" flag so two
  // back-to-back enqueue() calls in the same tick don't both spawn
  // a playNext race.
  const inFlightRef = useRef<boolean>(false);

  const releaseActive = useCallback(() => {
    if (activeAudioRef.current !== null) {
      try {
        activeAudioRef.current.pause();
        activeAudioRef.current.currentTime = 0;
      } catch {
        // noop
      }
      activeAudioRef.current = null;
    }
    if (activeRevokeRef.current !== null) {
      activeRevokeRef.current();
      activeRevokeRef.current = null;
    }
  }, []);

  const playNext = useCallback(async () => {
    if (services === null || !services.voice.isAvailable()) {
      queueRef.current = [];
      inFlightRef.current = false;
      setState('idle');
      return;
    }
    const next = queueRef.current.shift();
    if (next === undefined) {
      inFlightRef.current = false;
      setState('idle');
      return;
    }
    const token = cancelTokenRef.current;
    inFlightRef.current = true;
    // V4.7 (2026-06-02): 'loading' during the Kokoro fetch round-trip
    // so the UI shows a spinner. Flips to 'playing' only after
    // audio.play() actually runs.
    setState('loading');
    let result;
    try {
      result = await services.voice.speak(next.text, {
        voice: next.voice ?? defaultVoice,
      });
    } catch {
      if (cancelTokenRef.current === token) {
        void playNext();
      } else {
        inFlightRef.current = false;
      }
      return;
    }
    if (cancelTokenRef.current !== token) {
      result.revoke();
      inFlightRef.current = false;
      return;
    }
    const audio = new Audio(result.audioUrl);
    activeAudioRef.current = audio;
    activeRevokeRef.current = result.revoke;
    audio.onended = () => {
      if (activeRevokeRef.current !== null) {
        activeRevokeRef.current();
        activeRevokeRef.current = null;
      }
      activeAudioRef.current = null;
      void playNext();
    };
    audio.onerror = () => {
      if (activeRevokeRef.current !== null) {
        activeRevokeRef.current();
        activeRevokeRef.current = null;
      }
      activeAudioRef.current = null;
      void playNext();
    };
    void audio
      .play()
      .then(() => {
        // Confirm we're still the current item before flipping the
        // state — a cancelAll mid-play() resolution would have bumped
        // the cancel token.
        if (cancelTokenRef.current === token) {
          setState('playing');
        }
      })
      .catch(() => {
        audio.onended = null;
        if (activeRevokeRef.current !== null) {
          activeRevokeRef.current();
          activeRevokeRef.current = null;
        }
        activeAudioRef.current = null;
        void playNext();
      });
  }, [services, defaultVoice]);

  const enqueue = useCallback(
    (text: string, opts?: TtsEnqueueOptions) => {
      const trimmed = (text ?? '').trim();
      if (trimmed.length === 0) {
        return;
      }
      queueRef.current.push({
        text: trimmed,
        voice: opts?.voice,
        source: opts?.source,
      });
      if (!inFlightRef.current) {
        void playNext();
      }
    },
    [playNext],
  );

  const cancelAll = useCallback(() => {
    cancelTokenRef.current += 1;
    queueRef.current = [];
    inFlightRef.current = false;
    releaseActive();
    setState('cancelled');
    // immediate transition back to 'idle' on next tick so callers
    // can re-enqueue cleanly.
    queueMicrotask(() => {
      setState((s) => (s === 'cancelled' ? 'idle' : s));
    });
  }, [releaseActive]);

  useEffect(() => {
    return () => {
      cancelTokenRef.current += 1;
      queueRef.current = [];
      releaseActive();
    };
  }, [releaseActive]);

  return {
    state,
    enqueue,
    cancelAll,
    /** Test-only — observe queue length without exposing the ref. */
    pendingCount: () => queueRef.current.length,
  };
}
